import { NgClass, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { isNil } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  interval,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import {
  HasPermissionDirective,
  triggerIfSlow,
  UserService,
  WebSocketService,
} from '@ipg/ngx-frontend-toolset';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DeviceSelectionDialogServiceService } from '../../components/device-selection-dialog/services/device-selection-dialog-service.service';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { GlobalLoadersService } from '../../global-loaders.service';
import {
  DEVICE_TYPE_LABELS,
  Device,
  DeviceConnectionPayload,
  DeviceTelemetryPayload,
  DeviceWebSocketPayload,
  isIvyFamilyType,
} from '../../models/devices.model';
import { User } from '../../models/user.model';
import { FormatIntervalPipe } from '../../pipes/format-interval.pipe';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { DevicesService } from '../../services/devices.service';
import { TableLimitService } from '../../services/table-limit.service';
import { adjustPageIfNeeded } from '../../utils/pagination';
import { UnitService } from '../units/services/unit.service';
import { UnitResponseModel } from '../units/units.model';
import { ClientDevicesService } from './services/client-devices.service';

@Component({
  selector: 'app-client-devices',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatChipListbox,
    MatChipOption,
    MatPaginator,
    MatTableModule,
    MatTooltip,
    MatSort,
    MatSortHeader,
    DataTableContainerComponent,
    MapFromPipe,
    TitleCasePipe,
    NgClass,
    FormatIntervalPipe,
    MatInput,
    MatFormField,
    MatPrefix,
    MatSuffix,
    FormsModule,
    HasPermissionDirective,
  ],
  templateUrl: './client-devices.component.html',
  styleUrl: './client-devices.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientDevicesComponent {
  private static readonly LOADER_CALLER_ID = 'client_devices_loader_caller';
  private static readonly UNITS_LOADER_CALLER_ID = 'client_devices_units_loader_caller';

  private readonly _dialog = inject(DialogService);
  private readonly _deviceSelection = inject(DeviceSelectionDialogServiceService);
  private readonly _devices = inject(DevicesService);
  private readonly _clientDevices = inject(ClientDevicesService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _wsService = inject(WebSocketService);
  private readonly _tableLimit = inject(TableLimitService);
  private readonly _units = inject(UnitService);
  private readonly _userService = inject<UserService<User>>(UserService);
  private readonly _snackBar = inject(MatSnackBar);

  private readonly _clientId$ = this._route.parent!.paramMap.pipe(
    map((params) => params.get('clientId')),
    filter((clientId) => clientId != null)
  );

  private readonly _fetch$ = new Subject<void>();

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this._query.update((currentState) => ({ ...currentState, page: 1 })))
  );

  protected readonly keyword = signal<string | null>(null);

  private readonly _hasLoadedData = signal<boolean>(false);

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'label',
    sortOrder: 'asc',
  });

  protected readonly activeCategory = signal<'robot' | 'access-point'>('robot');

  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    keyword: this._debouncedKeyword$.pipe(startWith(this.keyword())),
    category: toObservable(this.activeCategory),
    __fetch: this._fetch$.pipe(startWith(undefined)),
  }).pipe(
    map(({ query, sort, keyword, category }) => ({
      ...query,
      ...sort,
      category,
      ...(keyword ? { q: keyword.trim() } : null),
    })),
    debounceTime(0)
  );

  private readonly _paginatedDevices$ = this._params$.pipe(
    withLatestFrom(this._clientId$),
    switchMap(([params, clientId]) =>
      this._clientDevices
        .getAll(clientId, {
          params,
        })
        .pipe(
          tap({
            error: () =>
              this._dialog.alert({
                title: `Foutmelding`,
                message: `Er is iets misgegaan bij het ophalen van de apparaten. Probeer het later nog eens`,
                confirmText: `OK`,
              }),
          }),
          takeUntilDestroyed(this._destroyRef),
          triggerIfSlow(() =>
            this._globalLoaders.setActive(
              ClientDevicesComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          ),
          finalize(() =>
            this._globalLoaders.setInactive(
              ClientDevicesComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          )
        )
    ),
    tap(() => this._hasLoadedData.set(true)),
    tap(({ meta }) => this._setPaginator({ page: meta.page, total: meta.total })),
    tap(({ meta }) => this._currentTotal.set(meta.total))
  );

  private readonly _devices$ = this._paginatedDevices$.pipe(map(({ items }) => items));

  private readonly _paginator = viewChild.required(MatPaginator);

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _globalLoaders = inject(GlobalLoadersService);

  private readonly _currentTotal = signal(0);

  private readonly _personalized_units$ = this._units.getByUser().pipe(
    map((response) => response.filter((value) => !value.deleteAt)),
    tap({
      error: () =>
        this._dialog.alert({
          title: `Foutmelding`,
          message: `Er is iets misgegaan bij het ophalen van de units. Probeer het later nog eens`,
          confirmText: `OK`,
        }),
    }),
    takeUntilDestroyed(this._destroyRef),
    triggerIfSlow(() =>
      this._globalLoaders.setActive(
        ClientDevicesComponent.UNITS_LOADER_CALLER_ID,
        HeaderComponent.LOADER_ID
      )
    ),
    finalize(() =>
      this._globalLoaders.setInactive(
        ClientDevicesComponent.UNITS_LOADER_CALLER_ID,
        HeaderComponent.LOADER_ID
      )
    )
  );

  protected readonly isAddDeviceDisabled = computed(
    () => this.activeCategory() === 'robot' && this._currentTotal() > 0
  );

  protected readonly robotTypeMap = DEVICE_TYPE_LABELS;

  // Single source of truth - signals handle reactivity automatically
  protected readonly dataSource = signal<Device[]>([]);

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly isSearchDisabled = computed(() => !this._hasLoadedData());

  protected readonly displayedColumns = computed(() =>
    this.activeCategory() === 'access-point'
      ? ['idAlias', 'label', 'type', 'serialNumber', 'unit', 'actions']
      : [
          'idAlias',
          'label',
          'type',
          'unit',
          'batteryLevel',
          'status',
          'duration',
          'actions',
        ]
  );

  protected readonly currentUnitId = computed(
    () => this._userService.data()?.unitId ?? null
  );

  protected readonly unitsByUser = signal<UnitResponseModel[]>([]);
  protected readonly unitMap = computed(
    () => new Map(this.unitsByUser().map((u) => [u._id, u.name]))
  );

  // Signal to trigger duration updates every second
  protected readonly currentTime = signal(Date.now());

  constructor() {
    // Subscribe to devices observable to populate the signal
    this._devices$.pipe(takeUntilDestroyed()).subscribe((devs) => {
      this.dataSource.set(devs);
    });

    this._personalized_units$.pipe(takeUntilDestroyed()).subscribe((units) => {
      this.unitsByUser.set(units);
    });

    // Update currentTime signal every second for real-time duration display
    interval(1000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.currentTime.set(Date.now());
      });

    // Set up WebSocket connection for device updates
    this._wsService
      .connect<DeviceWebSocketPayload>('/event-stream', {
        requireAuth: true,
        topics: ['device/#'],
      })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (connection) => {
          console.log('WebSocket connected for device updates');

          connection.events$.subscribe((event) => {
            console.log('Device WebSocket Event:', event);
            this._handleDeviceWebSocketEvent(event);
          });
        },
        error: (error) => console.error('WebSocket connection failed:', error),
      });
  }

  private _handleDeviceWebSocketEvent(event: {
    eventType: string;
    payload: DeviceWebSocketPayload;
  }): void {
    const { eventType, payload } = event;

    if (
      eventType === 'device.connection.open' ||
      eventType === 'device.connection.closed'
    ) {
      this._handleConnectionEvent(payload as DeviceConnectionPayload);
    } else if (eventType === 'device.telemetry') {
      this._handleTelemetryEvent(payload as DeviceTelemetryPayload);
    }
  }

  private _handleConnectionEvent(payload: DeviceConnectionPayload): void {
    this.dataSource.update((currentDevices) => {
      const deviceIndex = currentDevices.findIndex(
        (device) => device._id === payload.deviceId
      );

      if (deviceIndex === -1) {
        console.warn('Device not found in current list:', payload.deviceId);
        return currentDevices;
      }

      const updatedDevices = [...currentDevices];
      const device = { ...updatedDevices[deviceIndex] };

      // Update the device's session status
      if (device.latestSession) {
        device.latestSession = {
          ...device.latestSession,
          status: payload.status,
          lastSeenAt: payload.timestamp,
          closedAt: payload.status === 'closed' ? payload.timestamp : null,
          // Use connectedAt as the session start time for duration calculation
          createdAt: payload.connectedAt,
        };
      } else if (payload.status === 'open') {
        // Create a new session if device comes online and has no session
        device.latestSession = {
          id: payload.sessionId,
          createdAt: payload.connectedAt,
          updatedAt: payload.timestamp,
          closedAt: null,
          lastSeenAt: payload.timestamp,
          duration: 0,
          deviceId: payload.deviceId,
          status: 'open',
        };
      }

      updatedDevices[deviceIndex] = device;
      return updatedDevices;
    });
  }

  private _handleTelemetryEvent(payload: DeviceTelemetryPayload): void {
    this.dataSource.update((currentDevices) => {
      const deviceIndex = currentDevices.findIndex(
        (device) => device._id === payload.deviceId
      );

      if (deviceIndex === -1) {
        console.warn('Device not found in current list:', payload.deviceId);
        return currentDevices;
      }

      const updatedDevices = [...currentDevices];
      const device = { ...updatedDevices[deviceIndex] };

      // Update telemetry data based on device type
      if (isIvyFamilyType(device.type) && 'telemetry' in device.metadata) {
        device.metadata = {
          ...device.metadata,
          telemetry: {
            ...device.metadata.telemetry,
            ...payload.telemetry,
          },
        };
      } else if (
        (device.type === 'ozzy' || device.type === 'ozzyAndroid') &&
        'batteryLevel' in device.metadata
      ) {
        // For Ozzy devices, update batteryLevel if provided
        if (payload.telemetry.batteryLevel !== undefined) {
          device.metadata = {
            ...device.metadata,
            batteryLevel: payload.telemetry.batteryLevel,
          };
        }
      }

      // Update lastSeenAt if device has an active session
      if (device.latestSession && device.latestSession.status === 'open') {
        device.latestSession = {
          ...device.latestSession,
          lastSeenAt: payload.timestamp,
        };
      }

      updatedDevices[deviceIndex] = device;
      return updatedDevices;
    });
  }

  handleDeviceAttach() {
    this._deviceSelection.open(
      (result) =>
        of(result).pipe(
          filter((result) => result != undefined),
          map((value) => {
            if (!Array.isArray(value)) {
              return [value._id];
            }

            return value.map((value) => value._id);
          }),
          withLatestFrom(this._clientId$),
          map(([deviceIds, clientId]) => ({
            deviceIds: deviceIds,
            targetId: clientId,
            targetType: 'clients',
          })),
          switchMap((data) =>
            this._devices.attach(data).pipe(
              tap(() => this._fetch$.next()),
              catchError((error) => {
                return throwError(() => error);
              })
            )
          )
        ),
      this.activeCategory() === 'access-point',
      this.activeCategory()
    );
  }

  handleDeviceDetach(device: Device) {
    this._dialog
      .confirm({
        title: `${device.label ?? 'Apparaat'} loskoppelen?`,
        message:
          'Weet je zeker dat je dit apparaat wilt loskoppelen van de cliënt? Het apparaat wordt teruggeplaatst in de voorraad',
        confirmText: 'Bevestigen',
        cancelText: 'Annuleren',
        onConfirm: () =>
          this._devices.detach(device._id).pipe(
            tap(() =>
              adjustPageIfNeeded(this.dataSource, {
                paginator: this._paginator(),
                trigger: this._fetch$,
              })
            ),
            catchError((error) => {
              this._dialog.alert({
                title: 'Foutmelding',
                message: `Er is iets misgegaan bij het loskoppelen van het apparaat. Probeer het later nog eens`,
                confirmText: 'OK',
              });

              return throwError(() => error);
            })
          ),
      })
      .afterClosed()
      .subscribe({});
  }

  handleEditLabel(device: Device) {
    this._dialog.prompt(
      {
        title: 'Naam wijzigen',
        message: '',
        confirmText: 'Opslaan',
        cancelText: 'Annuleren',
        input: {
          mode: 'input',
          defaultValue: device.label ?? '',
          placeholder: 'Naam apparaat',
        },
        onConfirm: (result) =>
          of(result).pipe(
            filter(
              (result): result is string => !isNil(result) && result.trim().length > 0
            ),
            switchMap((label) =>
              this._devices.update(device._id, { label: label.trim() }).pipe(
                tap(() => this._fetch$.next()),
                catchError(() => {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message:
                      'Er is iets fout gegaan met het bewerken van de apparaatnaam. Probeer het later nog eens.',
                    confirmText: 'Begrepen',
                  });

                  return EMPTY;
                })
              )
            )
          ),
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  activateUnit(unitId: string) {
    const unitName = this.unitMap().get(unitId) ?? unitId;

    this._units
      .activate(unitId)
      .pipe(
        catchError(() => {
          this._dialog.alert({
            title: 'Activeren mislukt',
            message:
              'Er is iets misgegaan bij het activeren van de unit. Probeer het later nog eens.',
            confirmText: 'OK',
          });

          return EMPTY;
        }),
        switchMap(() => this._userService.fetch())
      )
      .subscribe(() => {
        this._snackBar.open(`Unit ${unitName} geactiveerd`, 'OK', {
          duration: 3000,
        });
        this._fetch$.next();
      });
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim());
    this._keywordInput$.next(value.trim());
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    const paginator = this._paginator();

    if (!paginator) {
      return;
    }

    paginator.pageIndex = page - 1;
    paginator.length = total;
  }
}
